/**
 * Frontend URL Generation Utility
 * Handles dynamic URL generation based on environment and context
 * 
 * Environments:
 * - development: localhost (VITE_BASE_DOMAIN=vidhyantra.local or localhost)
 * - staging: deployed UAT (VITE_NODE_ENV=staging, custom BASE_DOMAIN)
 * - production: production (VITE_NODE_ENV=production)
 */

import { normalizeVidhyantraDomain } from '@/utils/cookie-domain.util'
import { resolveDeployBaseDomainFromHostname } from '@/utils/deploy-domain.util'
import { isStagingDeployHostname } from '@/utils/staging-domain.util'

interface EnvironmentConfig {
  environmentType: 'development' | 'staging' | 'production';
  isProduction: boolean;
  isStaging: boolean;
  isDevelopment: boolean;
  isLocalhost: boolean;
  baseDomain: string;
  frontendUrl: string;
  backendUrl: string;
}

export class UrlGenerator {
  /**
   * Get environment variables
   */
  private static getEnv() {
    return {
      NODE_ENV: import.meta.env.VITE_NODE_ENV || import.meta.env.MODE,
      BASE_DOMAIN: import.meta.env.VITE_BASE_DOMAIN || 'localhost',
      FRONTEND_URL: import.meta.env.VITE_FRONTEND_URL,
      API_URL: import.meta.env.VITE_API_URL,
    };
  }

  /** Prefer live hostname over stale VITE_BASE_DOMAIN (e.g. vidyantra-dev.com on staging). */
  private static resolveBaseDomain(): string {
    if (typeof window !== 'undefined') {
      const fromHost = resolveDeployBaseDomainFromHostname(window.location.hostname)
      if (fromHost) return fromHost
    }

    const env = this.getEnv()
    return env.BASE_DOMAIN || 'localhost'
  }

  /**
   * Get environment type: 'development' | 'staging' | 'production'
   */
  static getEnvironmentType(): 'development' | 'staging' | 'production' {
    const env = this.getEnv();
    const hostname =
      typeof window !== 'undefined' ? normalizeVidhyantraDomain(window.location.hostname) : '';

    if (isStagingDeployHostname(hostname)) {
      return 'staging';
    }

    if (hostname === 'vidhyantra.com' || hostname.endsWith('.vidhyantra.com')) {
      return 'production';
    }
    
    // Development: VITE_BASE_DOMAIN === 'localhost' OR VITE_BASE_DOMAIN === 'vidhyantra.local' 
    // OR window.location.hostname includes 'localhost' or 'vidhyantra.local'
    if (env.BASE_DOMAIN === 'localhost' || 
        env.BASE_DOMAIN?.includes('vidhyantra.local') ||
        hostname.includes('localhost') ||
        hostname.includes('vidhyantra.local')) {
      return 'development';
    }
    
    // Production: VITE_NODE_ENV === 'production'
    if (env.NODE_ENV === 'production') {
      return 'production';
    }
    
    // Staging: VITE_NODE_ENV === 'staging' AND not development conditions
    if (env.NODE_ENV === 'staging') {
      return 'staging';
    }
    
    // Default to development for unknown environments
    return 'development';
  }

  /**
   * Check if running in localhost environment
   */
  private static isLocalhost(): boolean {
    const env = this.getEnv();
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    return env.BASE_DOMAIN === 'localhost' || 
           env.BASE_DOMAIN?.includes('vidhyantra.local') ||
           hostname === 'localhost' ||
           hostname.includes('vidhyantra.local');
  }

  /**
   * Get the base frontend URL for the current environment
   */
  static getFrontendBaseUrl(): string {
    const env = this.getEnv();
    const baseDomain = this.resolveBaseDomain();
    const envType = this.getEnvironmentType();

    if (env.FRONTEND_URL && !resolveDeployBaseDomainFromHostname()) {
      return env.FRONTEND_URL;
    }
    
    switch (envType) {
      case 'development':
        // Development: localhost or vidhyantra.local
        if (env.BASE_DOMAIN?.includes('vidhyantra.local')) {
          // Include port for vidhyantra.local in development
          const port = typeof window !== 'undefined' && window.location.port ? `:${window.location.port}` : ':3000';
          return `https://${env.BASE_DOMAIN}${port}`;
        }
        return 'http://localhost:3000';
        
      case 'staging':
        // Staging: deployed UAT
        return `https://${baseDomain}`;
        
      case 'production':
        // Production
        return `https://${baseDomain}`;
        
      default:
        return 'http://localhost:3000';
    }
  }
  
  /**
   * Get base domain logout URL (includes port for development)
   * Used for redirecting to parent domain logout page
   */
  static getBaseLogoutUrl(): string {
    const env = this.getEnv();
    const baseDomain = this.resolveBaseDomain();
    const envType = this.getEnvironmentType();
    
    switch (envType) {
      case 'development':
        // Development: localhost or vidhyantra.local
        if (env.BASE_DOMAIN?.includes('vidhyantra.local')) {
          const port = typeof window !== 'undefined' && window.location.port ? `:${window.location.port}` : ':3000';
          return `https://${env.BASE_DOMAIN}${port}/logout`;
        }
        return 'http://localhost:3000/logout';
        
      case 'staging':
        // Staging: deployed UAT
        return `https://${baseDomain}/logout`;
        
      case 'production':
        // Production
        return `https://${baseDomain}/logout`;
        
      default:
        return 'http://localhost:3000/logout';
    }
  }

  /**
   * Get the base backend URL for the current environment
   */
  static getBackendBaseUrl(): string {
    const env = this.getEnv();
    const baseDomain = this.resolveBaseDomain();

    if (env.API_URL && !resolveDeployBaseDomainFromHostname()) {
      return env.API_URL.replace('/api', ''); // Remove /api suffix if present
    }

    const envType = this.getEnvironmentType();
    
    switch (envType) {
      case 'development':
        // Development: localhost
        return 'http://localhost:8080';
        
      case 'staging':
        // Staging: deployed UAT
        return `https://api.${baseDomain}`;
        
      case 'production':
        // Production
        return `https://api.${baseDomain}`;
        
      default:
        return 'http://localhost:8080';
    }
  }

  /**
   * Get the API URL
   */
  static getApiUrl(): string {
    const env = this.getEnv();
    return env.API_URL || `${this.getBackendBaseUrl()}/api`;
  }

  /**
   * Generate platform-specific URLs
   */
  static getPlatformUrl(path: string = ''): string {
    const env = this.getEnv();
    const baseDomain = this.resolveBaseDomain();
    const envType = this.getEnvironmentType();

    switch (envType) {
      case 'development':
        // Development: localhost or vidhyantra.local (requires dnsmasq — see scripts/setup-local-dns.sh)
        if (env.BASE_DOMAIN?.includes('vidhyantra.local')) {
          const port = typeof window !== 'undefined' && window.location.port ? `:${window.location.port}` : ':3000';
          return `https://platform.${env.BASE_DOMAIN}${port}${path}`;
        }
        return `http://platform.localhost:3000${path}`;
        
      case 'staging':
        // Staging: deployed UAT
        return `https://platform.${baseDomain}${path}`;
        
      case 'production':
        // Production
        return `https://platform.${baseDomain}${path}`;
        
      default:
        return `http://platform.localhost:3000${path}`;
    }
  }

  /**
   * Generate organization-specific URLs
   */
  static getOrganizationUrl(orgSlug: string, path: string = ''): string {
    const env = this.getEnv();
    const baseDomain = this.resolveBaseDomain();
    const envType = this.getEnvironmentType();

    switch (envType) {
      case 'development':
        // Development: localhost or vidhyantra.local
        if (env.BASE_DOMAIN?.includes('vidhyantra.local')) {
          // Include port for vidhyantra.local in development
          const port = typeof window !== 'undefined' && window.location.port ? `:${window.location.port}` : ':3000';
          return `https://${orgSlug}.${env.BASE_DOMAIN}${port}${path}`;
        }
        return `http://${orgSlug}.localhost:3000${path}`;
        
      case 'staging':
        // Staging: deployed UAT
        return `https://${orgSlug}.${baseDomain}${path}`;
        
      case 'production':
        // Production
        return `https://${orgSlug}.${baseDomain}${path}`;
        
      default:
        return `http://${orgSlug}.localhost:3000${path}`;
    }
  }

  /**
   * Generate branch-specific URLs
   */
  static getBranchUrl(orgSlug: string, branchSlug: string, path: string = ''): string {
    const env = this.getEnv();
    const baseDomain = this.resolveBaseDomain();
    const envType = this.getEnvironmentType();

    switch (envType) {
      case 'development':
        // Development: localhost or vidhyantra.local
        if (env.BASE_DOMAIN?.includes('vidhyantra.local')) {
          // Include port for vidhyantra.local in development
          const port = typeof window !== 'undefined' && window.location.port ? `:${window.location.port}` : ':3000';
          return `https://${orgSlug}.${env.BASE_DOMAIN}${port}/${branchSlug}${path}`;
        }
        return `http://${orgSlug}.localhost:3000/${branchSlug}${path}`;
        
      case 'staging':
        // Staging: deployed UAT
        return `https://${orgSlug}.${baseDomain}/${branchSlug}${path}`;
        
      case 'production':
        // Production
        return `https://${orgSlug}.${baseDomain}/${branchSlug}${path}`;
        
      default:
        return `http://${orgSlug}.localhost:3000/${branchSlug}${path}`;
    }
  }

  /**
   * Generate magic link URLs based on invitation context
   */
  static getMagicLinkUrl(
    token: string,
    context: {
      type: 'platform' | 'organization' | 'branch';
      orgSlug?: string;
      branchSlug?: string;
    }
  ): string {
    let path = '';

    // Determine the verification path and URL based on context
    switch (context.type) {
      case 'platform':
        path = '/verifyplatformuseraccount';
        return this.getPlatformUrl(`${path}?token=${encodeURIComponent(token)}`);

      case 'organization':
        if (!context.orgSlug) {
          throw new Error('orgSlug is required for organization invitation URLs');
        }
        path = '/verifyorguseraccount';
        return this.getOrganizationUrl(
          context.orgSlug,
          `${path}?token=${encodeURIComponent(token)}`
        );

      case 'branch':
        if (!context.orgSlug || !context.branchSlug) {
          throw new Error('orgSlug and branchSlug are required for branch invitation URLs');
        }
        path = '/verifybranchuseraccount';
        return this.getBranchUrl(
          context.orgSlug,
          context.branchSlug,
          `${path}?token=${encodeURIComponent(token)}`
        );

      default:
        throw new Error(`Unknown invitation context type: ${(context as any).type}`);
    }
  }

  /**
   * Generate dashboard URLs based on user type
   */
  static getDashboardUrl(
    userType: 'platform' | 'organization',
    orgSlug?: string,
    branchSlug?: string
  ): string {
    switch (userType) {
      case 'platform':
        return this.getPlatformUrl('/dashboard');
      case 'organization':
        if (orgSlug && branchSlug) {
          return this.getBranchUrl(orgSlug, branchSlug, '/dashboard');
        }
        return this.getOrganizationUrl(orgSlug || 'default', '/dashboard');
      default:
        return this.getFrontendBaseUrl();
    }
  }

  /**
   * Same-origin onboarding route (use with React Router navigate).
   */
  static getOnboardingPath(): string {
    return '/onboarding';
  }

  /**
   * Absolute onboarding URL for full-page redirects (logout flows, cross-subdomain).
   */
  static getOnboardingUrl(): string {
    return `${this.getFrontendBaseUrl()}/onboarding`;
  }

  /**
   * Generate login URL for organization
   */
  static getLoginUrl(orgSlug?: string): string {
    if (orgSlug) {
      return this.getOrganizationUrl(orgSlug, '/login');
    }
    return this.getPlatformUrl('/login');
  }

  /**
   * Check if a URL is external (different domain)
   */
  static isExternalUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      const currentDomain = this.resolveBaseDomain();
      return !urlObj.hostname.includes(currentDomain);
    } catch {
      return false;
    }
  }

  /**
   * Get environment-specific configuration
   */
  static getEnvironmentConfig(): EnvironmentConfig {
    const envType = this.getEnvironmentType();
    
    return {
      environmentType: envType,
      isProduction: envType === 'production',
      isStaging: envType === 'staging',
      isDevelopment: envType === 'development',
      isLocalhost: this.isLocalhost(),
      baseDomain: this.resolveBaseDomain(),
      frontendUrl: this.getFrontendBaseUrl(),
      backendUrl: this.getBackendBaseUrl(),
    };
  }

  /**
   * Get current subdomain from window location
   */
  static getCurrentSubdomain(): string | null {
    const hostname = window.location.hostname;
    const baseDomain = this.resolveBaseDomain();

    // Handle localhost
    if (hostname.includes('localhost')) {
      const parts = hostname.split('.');
      if (parts.length > 1 && parts[0] !== 'localhost') {
        return parts[0];
      }
      return null;
    }

    // Handle production/development domains
    if (hostname.includes(baseDomain)) {
      const parts = hostname.split('.');
      const baseParts = baseDomain.split('.');
      
      // If hostname has more parts than base domain, first part is subdomain
      if (parts.length > baseParts.length) {
        return parts[0];
      }
    }

    return null;
  }

  /**
   * Check if current domain is main platform domain
   */
  static isMainPlatformDomain(): boolean {
    const subdomain = this.getCurrentSubdomain();
    return subdomain === 'platform' || subdomain === null;
  }

  /**
   * Get organization slug from current URL
   */
  static getOrganizationSlugFromUrl(): string | null {
    const subdomain = this.getCurrentSubdomain();
    
    // Exclude platform subdomain
    if (subdomain === 'platform' || subdomain === null) {
      return null;
    }

    return subdomain;
  }
}

